import React, {useEffect, useState} from "react";
import { UpdateFieldFn, Golfer } from "../types/types";
import { useLocation, useNavigate } from "react-router-dom";
import GiftSection from "./GiftSection";
import { getProducts, getSiteSettings } from "../api/api-hook";
import Footer from "./shared/Footer";
import { usePageContent } from "../api/usePageContent";


interface VipProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category:string;
  tabKey: string;
  imageUrl: string;
  under_25:string;
  product_id: string;
  bagTagPrice:number;
}

interface LocationState {
  golfer?: Golfer;
}

interface Step1Props {
  formData: Golfer;
  updateField: UpdateFieldFn;
}

const Step1: React.FC<Step1Props> = ({ formData, updateField }) => {
  const { title: pageTitle, overview: pageOverview } = usePageContent("step1");
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>("vip");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<VipProduct[]>([]);
  const [giftKey, setGiftKey] = useState(0);
  // The admin portal can hide the Gift tab; keep it hidden until the setting has loaded.
  const [giftTabVisible, setGiftTabVisible] = useState(false);

  useEffect(() => {
    getSiteSettings()
      .then((settings) => setGiftTabVisible(!!settings?.giftTabVisible))
      .catch(() => setGiftTabVisible(false));
  }, []);
  
  const state = (location.state || {}) as LocationState;

  console.log(state);
  console.log(location);

  const [golfer, setGolfer] = useState<Golfer | null>(
    state.golfer ?? null
  );

  const descriptionToList = (description: string) => {
    if (!description) return null;
  
    const blocks = description.split(/\r?\n\r?\n+/);
  
    return (
      <div className="mb-3">
        {blocks.map((block, index) => {
          const lines = block.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  
          if (lines.length === 1) {
            return (
              <div key={index} className="mb-2">
                {lines[0]}
              </div>
            );
          }
  
          return (
            <ul key={index} className="mb-3">
              {lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          );
        })}
      </div>
    );
  };

  const handleFieldChange = (field: keyof Golfer) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      updateField(field, value);
      setGolfer((prev) => {
        const base = prev ?? {};
        if (field === "vip_package") {
          return { ...base, vip_package: value };
        }
        if (field === "vip_card_type") {
          return { ...base, vip_card_type: value };
        }
        return base;
      });
    };

  useEffect(() => {
    async function loadProducts() {
      try {
        setLoading(true);
        setError(null);
        const raw = await getProducts({
          property: "product_category",
          value: "vip"
        });

        const data: VipProduct[] =
          typeof raw === "string" ? JSON.parse(raw) : raw;

          console.log(data);
        setProducts(data);

        if (data.length > 0) {
          const initialTabKey = state.golfer?.vip_package || data[0].tabKey;
          setActiveTabKey(initialTabKey);
          
          const initialProduct = data.find((p) => p.tabKey === initialTabKey);

          updateField("vip_package", initialTabKey);
          if (initialProduct) {
            updateField("product_id", initialProduct.product_id as any);
          }
        }

        console.log(data);

      } catch (err) {
        console.error(err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    }
  
    loadProducts();
  }, []);

  const handleTabSelect = (tabKey: string) => {
    if (activeTabKey === "gift" && tabKey !== "gift") {
      setGiftKey((prev) => prev + 1);
    }
    
    updateField("GiftPurchase" as keyof Golfer, false as any);
    updateField("GiftProductId" as keyof Golfer, "");
    updateField("GiftVipPackage" as keyof Golfer, "");

    const selected = products.find((p) => p.tabKey === tabKey);
  
    updateField("vip_package", tabKey);
    updateField("vip_card_type", tabKey === "wv-vip-card" ? "WV VIP Card" : "");

    if (selected) {
      updateField("product_id", selected.product_id as any);
    }

    setGolfer((prev) =>
      prev
        ? {
            ...prev,
            GiftPurchase: false,
            GiftProductId: "",
            GiftVipPackage: "",
          }
        : prev
    );
  
    setActiveTabKey(tabKey || "vip");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  
    const updatedGolfer: Golfer = {
      ...(golfer ?? {}),
      vip_package: formData.vip_package || "",
      vip_card_type: formData.vip_card_type || "",
      product_id: formData.product_id || "",
      GiftPurchase: (golfer?.GiftPurchase ?? formData.GiftPurchase) ?? false,
    };
  
    navigate("/step2", {
      state: {
        ...state,            
        golfer: updatedGolfer,
      },
    });
  };

  const handleGiftSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updatedGolfer: Golfer = {
      ...(golfer ?? {}),
      vip_package: formData.vip_package || "",
      vip_card_type: formData.vip_card_type || "",
      product_id: formData.product_id || "",
      GiftPurchase: (golfer?.GiftPurchase ?? formData.GiftPurchase) ?? false,
    };
  
    navigate("/step6", {
      state: {
        ...state,            
        golfer: updatedGolfer,
      },
    });
  };

  const handleGiftChange = (hasGift: boolean) => {
    updateField("GiftPurchase" as keyof Golfer, hasGift as any);
    setGolfer((prev) => {
      const base = prev ?? {};
      return {
        ...base,
        GiftPurchase: hasGift,
      };
    });
  };

  const handleGiftProductChange = (productId: string | null) => {
    updateField("GiftProductId" as keyof Golfer, productId ?? "");
    setGolfer((prev) => {
      const base = prev ?? {};
      return {
        ...base,
        GiftProductId: productId ?? "",
      };
    });
  };

  const handleGiftTabChange = (tabKey: string | null) => {
    updateField("GiftVipPackage" as keyof Golfer, tabKey ?? "");
    setGolfer((prev) => {
      const base = prev ?? {};
      return {
        ...base,
        GiftVipPackage: tabKey ?? "",
      };
    });
  };

  return (
    <div className="container mt-5">
      <section className="vip-levels vip-step1 container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div className="text-center col-12 mb-4">
              <h3 className="mb-3 heading">{pageTitle || "Choose Your Experience"}</h3>
              {(pageOverview || "All VIP Card purchases include access to golf. Bronze, Silver and Gold packages feature additional benefits, including a Handicap Index® through the VSGA eClub.") && (
                <p className="lead col-8 text-center" style={{margin:'auto auto'}}>
                  {pageOverview || "All VIP Card purchases include access to golf. Bronze, Silver and Gold packages feature additional benefits, including a Handicap Index® through the VSGA eClub."}
                </p>
              )}
            </div>

            {loading && (
              <div className="text-center my-4">
                <div className="spinner-border" role="status" />
                <p className="mt-2 text-muted">Loading VIP options…</p>
              </div>
            )}

            {error && (
              <div className="alert alert-danger mt-3 text-center">
                {error}
              </div>
            )}

            <div className="inner">
              <div className="nav nav-tabs vip-tab-bar justify-content-center mb-4" id="vipTabs" role="tablist">
                {products.map((product, index) => (
                  <button
                    className={"vip-tab " + (activeTabKey === product.tabKey ? "active" : "")}
                    id={`${product.tabKey}-tab`}
                    type="button"
                    role="tab"
                    tabIndex={index}
                    aria-controls={product.tabKey}
                    aria-selected={activeTabKey === product.tabKey}
                    onClick={() => handleTabSelect(product.tabKey)}
                    key={product.id}
                  >
                    {product.label}
                  </button>
                ))}
                {!loading && giftTabVisible && (
                  <button
                    className={"vip-tab " + (activeTabKey === "gift" ? "active" : "")}
                    id="gift-tab"
                    type="button"
                    role="tab"
                    aria-controls="gift"
                    aria-selected={activeTabKey === "gift"}
                    onClick={() => setActiveTabKey("gift")}
                  >
                    Gift
                  </button>
                )}
              </div>
            </div>

            <div className="tab-content pt-3 mt-3" id="vipTabsContent">
              {products.map((product) => (
                <div
                  key={product.tabKey}
                  className={"tab-pane fade " + (activeTabKey === product.tabKey ? "show active" : "")}
                  id={product.tabKey}
                  role="tabpanel"
                  aria-labelledby={`${product.tabKey}-tab`}
                >
                  <div className="gradient-overlay"></div>
                  <div className="row align-items-center g-4">
                    <div className="col-md-6 content">
                      <h3 className="mb-2">{product.name} - ${product.price?.toFixed(2)}</h3>

                      {product.description && (
                        <div className="text-muted mb-3 mt-3" id="tab">{descriptionToList(product.description)}</div>
                      )}

                      <form onSubmit={handleSubmit} className="row gy-2 gx-2 align-items-end">
                        {product.under_25 && (
                          <div className="form-check mt-2">
                            <input
                              type="checkbox"
                              id="age"
                              className="form-check-input"
                              required
                            />
                            <label htmlFor="ageVerify" className="form-check-label">
                              I confirm this package is for someone age 18–25.
                            </label>
                          </div>
                        )}

                        <div className="mt-4 pb-5">
                          <input type="hidden" name="vip_package" id="vip_package" value={formData.vip_package || "vip"} onChange={handleFieldChange("vip_package")}/>
                          <input type="hidden" name="product_id" id="product_id" value={formData.product_id} />
                          {product.tabKey !== "wv-vip-card" && (
                            <div className="col-12 col-sm-7">
                              <label htmlFor="vip-format" className="form-label mb-1">How would you like to use your VIP card?</label>
                              <select
                                id="vip_card_type"
                                name="vip_card_type"
                                className="form-select"
                                value={formData.vip_card_type}
                                onChange={handleFieldChange("vip_card_type")}
                                required
                              >
                                <option value="">Select format…</option>
                                <option value="Paper">{product.name} - Booklet</option>
                                <option value="Digital">{product.name} - Digital</option>
                              </select>
                            </div>
                          )}
                          <div className="col-12 col-sm-5 mt-5 mt-3">
                            <button type="submit" className="btn btn-primary w-100">
                              Continue
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>

                    <div className="col-md-6 text-end vip-product-image order-first order-md-last">
                      <img
                        src={product.imageUrl}
                        alt="VSGA `{product.name}'"
                        className="img-fluid shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div
                className={"tab-pane fade " + (activeTabKey === "gift" ? "show active" : "")}
                id="gift"
                role="tabpanel"
                aria-labelledby="gift-tab"
              >
                <div className="gradient-overlay"></div>
                <div className="row g-4">
                  <div className="col-12 content">
                    <form onSubmit={handleGiftSubmit} className="row gy-2 gx-2 align-items-end">
                      {/* KEY FIX: only mount GiftSection when gift tab is active */}
                      {activeTabKey === "gift" && (
                        <GiftSection
                          key={giftKey}
                          onGiftChange={handleGiftChange}
                          onGiftProductChange={handleGiftProductChange}
                          onGiftTabChange={handleGiftTabChange}
                        />
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </section>
    </div>
  );
};

export default Step1;